import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { COMPANY, MARKETS, MARKET_ORDER, PRICES, PRICE_NOTE, sized } from "@/lib/content";
import { Reveal } from "@/components/public/Reveal";
import { Btn, TextLink, SectionHead, Photo, PriceRow, CTA, DimLine, Eyebrow } from "@/components/public/ui";
import { Hero } from "@/components/public/Hero";
import { ProjectTile } from "@/components/public/ProjectCard";
import { Skeleton } from "@/components/ui/skeleton";

export default function MarketPage({ slug }) {
  const m = MARKETS[slug];
  const [projects, setProjects] = useState(null);

  useEffect(() => {
    if (!m) return;
    setProjects(null);
    api.get(`/public/projects?market=${m.key}`).then((r) => setProjects(r.data)).catch(() => setProjects([]));
  }, [slug, m]);

  if (!m) return <Navigate to="/" replace />;
  const others = MARKET_ORDER.filter((s) => s !== slug);
  const prices = m.showPrices ? PRICES.filter((p) => p.market === m.key) : [];

  return (
    <div data-testid={`public-market-${slug}`}>
      <Hero
        title={m.title}
        lead={m.lead}
        image={sized(m.hero, 2400)}
        imageAlt=""
        note={<span data-testid="public-market-contact">Telefon <a href={COMPANY.phoneHref} className="u-link tabular font-medium text-foreground">{COMPANY.phone}</a> · <a href={`mailto:${COMPANY.email}`} className="u-link font-medium text-foreground">{COMPANY.email}</a></span>}
        actions={
          <>
            <Btn to="/kontakt" size="lg" arrow data-testid="public-market-bestill-button">Send førespurnad</Btn>
            {m.showPrices && <Btn to="/prisar" variant="outline" size="lg" data-testid="public-market-prices-button">Sjå prisar</Btn>}
          </>
        }
      />

      {/* Services, numbered */}
      <section className="container-public pt-24 sm:pt-32" data-testid="public-market-services">
        <SectionHead eyebrow="Tenester" title="Dette gjer vi" text={m.desc} />
        <DimLine className="mt-10" />
        <ol className="grid sm:grid-cols-2 lg:grid-cols-3">
          {m.services.map(([t, d], i) => (
            <li key={t} className="border-b border-border py-7 sm:pr-10 lg:[&:nth-child(n+4)]:border-b-0">
              <span className="t-mono">0{i + 1}</span>
              <h3 className="mt-3 text-[18px] font-semibold text-ink">{t}</h3>
              <p className="mt-2 text-[15px] leading-relaxed text-muted-foreground">{d}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* Intro text + portrait image */}
      <section className="container-public pt-24 sm:pt-32" data-testid="public-market-intro">
        <div className="grid gap-10 lg:grid-cols-12 lg:gap-16">
          <Reveal className="lg:col-span-7">
            <h2 className="t-h2 text-ink">{m.introTitle}</h2>
            <p className="mt-6 max-w-2xl text-[17px] leading-relaxed text-muted-foreground">{m.intro}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Btn to="/kontakt" variant="dark" size="md" arrow data-testid="public-market-intro-cta">Ta kontakt</Btn>
              <Btn to="/om-oss" variant="outline" size="md">Om oss</Btn>
            </div>
          </Reveal>
          <Reveal delay={0.06} className="lg:col-span-5">
            <Photo src={sized(m.detail, 1200)} alt="" ratio="aspect-[4/5]" />
          </Reveal>
        </div>
      </section>

      {/* Prices (service) */}
      {prices.length > 0 && (
        <section className="container-public pt-24 sm:pt-32" data-testid="public-market-prices">
          <div className="rounded-pub bg-paper p-7 sm:p-10 lg:p-14">
            <SectionHead eyebrow="Prisar" title="Pris på vanlege oppdrag" text="Døme på prisar for dei vanlegaste jobbane heime hos folk." action={<TextLink to="/prisar">Sjå alle prisar</TextLink>} />
            <ul className="mt-8 divide-y divide-border border-y border-border">
              {prices.slice(0, 6).map((item) => (<PriceRow key={item.id} item={item} compact />))}
            </ul>
            <p className="caption mt-4" data-testid="public-demo-note">{PRICE_NOTE}</p>
          </div>
        </section>
      )}

      {/* Band image */}
      <section className="container-public pt-24 sm:pt-32">
        <Photo src={sized(m.band, 2400)} alt="" ratio="aspect-[4/3] sm:aspect-[21/9]" sizes="100vw" />
      </section>

      {/* Projects */}
      <section className="container-public pt-24 sm:pt-32" data-testid="public-market-projects">
        <SectionHead eyebrow="Prosjekt" title={`Prosjekt innan ${m.title.toLowerCase()}`} action={<TextLink to="/prosjekt">Sjå alle prosjekt</TextLink>} />
        <DimLine className="mt-10" />
        <div className="grid gap-x-6 gap-y-10 pt-8 sm:grid-cols-2 lg:grid-cols-3">
          {projects === null && [0, 1, 2].map((i) => (
            <div key={i}><Skeleton className="aspect-[4/3] w-full rounded-pub" /><Skeleton className="mt-4 h-6 w-2/3" /></div>
          ))}
          {projects && projects.length === 0 && <p className="text-muted-foreground" data-testid="public-market-projects-empty">Vi legg ut prosjekt innan {m.title.toLowerCase()} snart. Ta kontakt for referansar.</p>}
          {projects?.map((p, i) => (
            <Reveal key={p.slug} delay={(i % 3) * 0.05}><ProjectTile project={p} /></Reveal>
          ))}
        </div>
      </section>

      {/* Other markets */}
      <section className="container-public pt-24 sm:pt-32" data-testid="public-market-others">
        <Eyebrow>Andre fagområde</Eyebrow>
        <ul className="mt-5 grid gap-x-8 sm:grid-cols-3">
          {others.map((s) => (
            <li key={s} className="border-t border-border">
              <Link to={`/${s}`} className="group flex items-start justify-between gap-4 py-5" data-testid={`public-market-other-${s}`}>
                <div>
                  <p className="text-[18px] font-semibold text-ink">{MARKETS[s].title}</p>
                  <p className="mt-1 text-[14px] leading-snug text-muted-foreground">{MARKETS[s].short}</p>
                </div>
                <ArrowUpRight className="mt-1 h-5 w-5 shrink-0 text-ink/30 transition-colors group-hover:text-ink" />
              </Link>
            </li>
          ))}
        </ul>
      </section>

      <div className="pt-24 sm:pt-32">
        <CTA />
      </div>
    </div>
  );
}
